import { useCallback, useState } from 'react';
import { Button } from 'primereact/button';
import { RColumn, RemoteTable } from 'medere-ui-framework';
import {
    DrugTagCostSort,
    DrugTagCostSortField,
    DrugTagPrescriptionCostRowDTO,
    DrugTagPrescriptionReportFilter,
} from '../../dto/DrugTagPrescriptionReportDTO';
import { getDrugTagPrescriptionCost } from '../../services/DrugTagPrescriptionReportController';
import { formatMoney } from '../../utils/money';
import DrugTagCostDetailDrawer from './DrugTagCostDetailDrawer';
import TwoLines from './TwoLines';

const integer = new Intl.NumberFormat('es-AR');

/** Costo arriba y dosis abajo, en la misma celda: así la tabla entra sin scroll horizontal. */
function costCell(cost: number | null, doses: number) {
    return <TwoLines main={formatMoney(cost)} detail={`${integer.format(doses)} dosis`} alignEnd />;
}

// Cuerpos de las columnas a nivel de módulo: RemoteTable necesita funciones estables.
type Row = DrugTagPrescriptionCostRowDTO;
const text = (value: string | null) => (value ? value : '—');
const healthEntityBody = (row: Row) => text(row.healthEntityName);
/** El medicamento y, debajo, la presentación; lo aplicado sin nada preparado va en su propia fila. */
const drugBody = (row: Row) => (
    <TwoLines main={text(row.drugName)}
        detail={row.presentation === null ? 'Sin preparación registrada' : row.presentationName} />
);
const appliedBody = (row: Row) => costCell(row.appliedCost, row.applied);
const notAppliedBody = (row: Row) => costCell(row.notAppliedPreparedCost, row.notAppliedPrepared);
const suspendedBody = (row: Row) => costCell(row.suspendedPreparedCost, row.suspendedPrepared);
const pendingBody = (row: Row) => costCell(row.pendingPreparedCost, row.pendingPrepared);
const discardedBody = (row: Row) => costCell(row.discardedCost, row.discarded);
const totalBody = (row: Row) => costCell(row.totalCost, row.preparedTotal);

/**
 * Anchos fijos. Obra social y Medicamento quedan fijas a la izquierda si la pantalla es más angosta que la tabla. El
 * ancho mínimo de la tabla es la suma: si fuera menor, el framework comprime columnas hasta 0 px.
 */
const WIDTHS = { healthEntity: 200, drug: 260, category: 150 };
const CATEGORIES = 6;
const TABLE_MIN_WIDTH = WIDTHS.healthEntity + WIDTHS.drug + CATEGORIES * WIDTHS.category;
const HEALTH_ENTITY_STYLE = { width: WIDTHS.healthEntity };
const DRUG_STYLE = { width: WIDTHS.drug };
const CATEGORY_STYLE = { width: WIDTHS.category };
// text-right: con eso la tabla del framework también alinea la cabecera.
const MONEY = 'text-right';
/** Sin otro orden pedido, el backend pone primero lo más costoso: la grilla lo muestra marcado. */
const DEFAULT_SORT = [{ field: 'totalCost', order: -1 as const }];

/** Lo que manda el RemoteTable al pedir una página cuando se ordena tocando un encabezado. */
interface TableParams { multiSortMeta?: { fieldName: string; ascOrder: boolean }[] }

function sortOf(params?: TableParams): DrugTagCostSort {
    const first = params?.multiSortMeta?.[0];
    return first ? { sortField: first.fieldName as DrugTagCostSortField, ascOrder: first.ascOrder } : {};
}

interface DrugTagCostTableProps {
    filter: DrugTagPrescriptionReportFilter;
    /** Orden actual de la grilla, para exportar en el mismo orden. */
    onSortChange: (sort: DrugTagCostSort) => void;
    /** Error al traer una página: lo muestra la vista. */
    onError: (error: unknown) => void;
}

/**
 * Tab Costo: por obra social y medicamento, lo aplicado y lo preparado sin aplicar (no aplicadas, suspendidas y
 * pendientes), cada uno con su costo y sus dosis, y el total. Cada categoría se ordena por su costo; arranca con lo
 * más costoso primero. El ojo abre las dosis de la fila.
 */
export default function DrugTagCostTable({ filter, onSortChange, onError }: DrugTagCostTableProps) {
    /** Fila cuyo detalle está abierto en el panel lateral. */
    const [viewing, setViewing] = useState<Row | null>(null);

    const rowActions = useCallback((row: Row) => (
        <Button
            type="button"
            text
            className="nv-icon nv-tip"
            icon="pi pi-eye"
            aria-label={`Ver detalle de ${row.drugName ?? 'la fila'} en ${row.healthEntityName ?? 'la obra social'}`}
            data-pr-tooltip="Ver detalle"
            onClick={() => setViewing(row)}
        />
    ), []);

    function loadPage(offset: number, pageSize: number, params?: TableParams): Promise<Row[]> {
        const sort = sortOf(params);
        onSortChange(sort);
        return getDrugTagPrescriptionCost(filter, sort, offset, pageSize).catch((error: unknown) => {
            onError(error);
            return [];
        });
    }

    return (
        <div className="drug-tag-cost-table">
            <RemoteTable
                idTable="drugTagPrescriptionCost"
                dataKey="rowKey"
                getItems={loadPage}
                params={filter}
                paramsDelay={0}
                filterRow={false}
                defaultMultiSortMeta={DEFAULT_SORT}
                rowActions={rowActions}
                notFlex
                maxHeight={560}
                tableMinWidth={TABLE_MIN_WIDTH}
                notFoundText="No hay dosis aplicadas ni preparadas para estos filtros."
            >
                <RColumn field="healthEntityName" header="Obra social" sortable frozen alignFrozen="left"
                    style={HEALTH_ENTITY_STYLE} body={healthEntityBody} />
                <RColumn field="drugName" header="Medicamento" sortable frozen alignFrozen="left" style={DRUG_STYLE}
                    body={drugBody} />
                <RColumn field="appliedCost" header="Aplicadas" sortable className={MONEY} style={CATEGORY_STYLE}
                    body={appliedBody} />
                <RColumn field="notAppliedPreparedCost" header="No aplicadas preparadas" sortable className={MONEY}
                    style={CATEGORY_STYLE} body={notAppliedBody} />
                <RColumn field="suspendedPreparedCost" header="Suspendidas preparadas" sortable className={MONEY}
                    style={CATEGORY_STYLE} body={suspendedBody} />
                <RColumn field="pendingPreparedCost" header="Pendientes preparadas" sortable className={MONEY}
                    style={CATEGORY_STYLE} body={pendingBody} />
                <RColumn field="discardedCost" header="Descartadas" sortable className={MONEY} style={CATEGORY_STYLE}
                    body={discardedBody} />
                <RColumn field="totalCost" header="Total" sortable className={MONEY} style={CATEGORY_STYLE}
                    body={totalBody} />
            </RemoteTable>
            {viewing && (
                <DrugTagCostDetailDrawer
                    key={viewing.rowKey}
                    filter={filter}
                    row={viewing}
                    onHide={() => setViewing(null)}
                    onError={onError}
                />
            )}
        </div>
    );
}
